/**
 * Editor settings (review D-3): tool, snapping, show flags, view mode, camera
 * speed, realtime. One schema-described object, changed through one command.
 *
 * Settings reuse the component prop types, so validation and the Preferences
 * UI come from the same reflection as the Inspector (review D-4). Each setting
 * declares whether it persists across visits (camera speed does; the active
 * tool is session state, as in Unreal).
 */
import { parsePayload } from "../../commands/payload";
import type { CommandDefinition } from "../../commands/types";
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import { defaultForProp, validateProp } from "../../world/schema/propTypes";
import type { PropType } from "../../world/schema/propTypes";
import type { PropValue } from "../../world/schema/defineComponent";

export interface SettingDefinition {
  readonly prop: PropType;
  readonly persist: boolean;
  /** Preferences dialog grouping. */
  readonly category: "Viewport" | "Snapping" | "Show" | "Navigation" | "Levels";
}

// `const P` keeps enum options literal, so SettingsValues infers exact types
// (e.g. tool: "select" | "translate" | …).
function setting<const P extends PropType>(
  prop: P,
  persist: boolean,
  category: SettingDefinition["category"],
): SettingDefinition & { readonly prop: P } {
  return { prop, persist, category };
}

export const SETTINGS = {
  tool: setting(
    {
      type: "enum",
      label: "Tool",
      options: [
        { value: "select", label: "Select" },
        { value: "translate", label: "Move" },
        { value: "rotate", label: "Rotate" },
        { value: "scale", label: "Scale" },
      ],
    },
    false,
    "Viewport",
  ),
  viewMode: setting(
    {
      type: "enum",
      label: "View mode",
      options: [
        { value: "lit", label: "Lit" },
        { value: "unlit", label: "Unlit" },
        { value: "wireframe", label: "Wireframe" },
      ],
    },
    false,
    "Viewport",
  ),
  // Unreal's viewport Realtime: effects and animations play on their own. Automatic plays
  // them unless the system asks for reduced motion; On and Off are the visitor's choice,
  // which beats that default (see ambientEffects).
  realtime: setting(
    {
      type: "enum",
      label: "Realtime",
      options: [
        { value: "auto", label: "Automatic" },
        { value: "on", label: "On" },
        { value: "off", label: "Off" },
      ],
      default: "auto",
    },
    true,
    "Viewport",
  ),
  // Unreal's Game View (G): the level without editor overlays (grid, selection,
  // gizmo, labels, stats). A momentary view, so not kept across visits.
  gameView: setting({ type: "bool", label: "Game View" }, false, "Viewport"),
  // Off by default: characters and effects keep animating. On, the viewport
  // stops drawing 10 s after the last input (SPEC §10's idle pause, as an option).
  pauseWhenIdle: setting({ type: "bool", label: "Pause viewport when idle" }, true, "Viewport"),
  // Selection reads as the outline alone, as in Unreal; the screen-space bracket
  // from reference 02 is an opt-in preference.
  showSelectionBracket: setting({ type: "bool", label: "Selection bracket" }, true, "Viewport"),
  // Off by default: a marquee takes what it touches. On, only what it fully
  // contains, like Unreal's "Use Strict Box Selection".
  strictBoxSelection: setting({ type: "bool", label: "Strict box selection" }, true, "Viewport"),
  snapEnabled: setting({ type: "bool", label: "Snap", default: true }, true, "Snapping"),
  snapTranslate: setting(
    { type: "number", label: "Grid snap", min: 1, max: 1000, step: 1, unit: "cm", default: 10 },
    true,
    "Snapping",
  ),
  snapRotate: setting(
    { type: "number", label: "Rotation snap", min: 1, max: 90, step: 1, unit: "°", default: 15 },
    true,
    "Snapping",
  ),
  snapScale: setting(
    { type: "number", label: "Scale snap", min: 0.01, max: 10, step: 0.05, default: 0.25 },
    true,
    "Snapping",
  ),
  showGrid: setting({ type: "bool", label: "Grid", default: true }, true, "Show"),
  showBounds: setting({ type: "bool", label: "Bounds" }, true, "Show"),
  showWireframe: setting({ type: "bool", label: "Wireframe" }, true, "Show"),
  // Camera gizmos, as Unity and Unreal draw them; a selected camera also shows its frustum.
  showCameras: setting({ type: "bool", label: "Cameras", default: true }, true, "Show"),
  showAllFrustums: setting({ type: "bool", label: "All camera frustums" }, true, "Show"),
  // The viewport stats readout (frame rate, draws, triangles), like Unreal's stat unit.
  // On by default: it shows the engine at work, and visitors wouldn't go looking for it.
  showStats: setting({ type: "bool", label: "Stats", default: true }, true, "Show"),
  // The readout's full table (`stat unit`), or one line (`stat fps`). Automatic opens
  // it on desktop and folds it on phones; a click on the readout picks one for good.
  statsDetails: setting(
    {
      type: "enum",
      label: "Stats details",
      options: [
        { value: "auto", label: "Automatic" },
        { value: "open", label: "Open" },
        { value: "folded", label: "Folded" },
      ],
      default: "auto",
    },
    true,
    "Show",
  ),
  cameraSpeed: setting(
    { type: "number", label: "Camera speed", min: 1, max: 8, step: 1, default: 4 },
    true,
    "Navigation",
  ),
  navHintDismissed: setting({ type: "bool", label: "Hide navigation hint" }, true, "Navigation"),
  // On by default: edits save themselves (Autosave), so they come back after a
  // reload too. Off, a reload starts from the authored levels.
  keepSavedEdits: setting(
    { type: "bool", label: "Keep saved edits across reloads", default: true },
    true,
    "Levels",
  ),
} as const satisfies Record<string, SettingDefinition>;

export type SettingKey = keyof typeof SETTINGS;
export type SettingsValues = {
  readonly [K in SettingKey]: PropValue<(typeof SETTINGS)[K]["prop"]>;
};

export interface SetSettingPayload {
  readonly key: SettingKey;
  readonly value: unknown;
}

export class EditorSettings implements ReadableStore<SettingsValues> {
  /** Handed only to `editor.setSetting` (CLAUDE.md rule 1). */
  readonly writer: { set(key: SettingKey, value: unknown): void };
  private readonly store: VersionedStore<SettingsValues>;

  /** `restored` are previously persisted values; anything unknown or invalid is ignored. */
  constructor(scheduler: ChangeScheduler, restored: Readonly<Record<string, unknown>> = {}) {
    this.store = new VersionedStore(scheduler, initialValues(restored));
    this.writer = {
      set: (key, value) => {
        this.store.publish({ ...this.store.getSnapshot(), [key]: value });
      },
    };
  }

  readonly subscribe = (listener: () => void) => this.store.subscribe(listener);
  readonly getSnapshot = (): SettingsValues => this.store.getSnapshot();

  /** Only the settings that persist across visits, for saving to storage. */
  persistedValues(): Partial<SettingsValues> {
    const values = this.store.getSnapshot();
    return Object.fromEntries(
      settingKeys()
        .filter((key) => SETTINGS[key].persist)
        .map((key) => [key, values[key]]),
    );
  }
}

export function settingsCommands(settings: EditorSettings): CommandDefinition[] {
  const setSetting: CommandDefinition<SetSettingPayload> = {
    type: "editor.setSetting",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { key: "string", value: "unknown" });
      if (!shape.ok) return shape;
      if (!isSettingKey(shape.value.key))
        return { ok: false, error: `Unknown setting '${shape.value.key}'` };

      const result = validateProp(SETTINGS[shape.value.key].prop, shape.value.value);
      if (!result.ok) return { ok: false, error: `${shape.value.key}: ${result.error}` };
      return { ok: true, value: { key: shape.value.key, value: result.value } };
    },
    do: (payload) => {
      const previous = settings.getSnapshot()[payload.key];
      settings.writer.set(payload.key, payload.value);
      return previous;
    },
    undo: (payload, previous) => {
      settings.writer.set(payload.key, previous);
    },
    describe: (payload) =>
      `Set ${SETTINGS[payload.key].prop.label}: ${formatValue(SETTINGS[payload.key].prop, payload.value)}`,
  };

  return [setSetting] as CommandDefinition[];
}

function initialValues(restored: Readonly<Record<string, unknown>>): SettingsValues {
  const values: Record<string, unknown> = {};
  for (const key of settingKeys()) {
    const prop = SETTINGS[key].prop;
    const candidate = restored[key];
    const parsed = candidate === undefined ? null : validateProp(prop, candidate);
    values[key] = parsed?.ok ? parsed.value : defaultForProp(prop);
  }
  return values as SettingsValues;
}

/** How a value reads in a log line: on/off, the enum label, or number + unit. */
function formatValue(prop: PropType, value: unknown): string {
  if (prop.type === "bool") return value ? "on" : "off";
  if (prop.type === "enum")
    return prop.options.find((o) => o.value === value)?.label ?? String(value);
  if (prop.type === "number") return `${String(value)}${prop.unit ?? ""}`;
  return String(value);
}

/** Every setting key, in declaration order (Preferences follows it). */
export function settingKeys(): SettingKey[] {
  return Object.keys(SETTINGS) as SettingKey[];
}

function isSettingKey(key: string): key is SettingKey {
  return Object.hasOwn(SETTINGS, key);
}
